<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
      </div>
    </div>
    <RecordsView
      :apiStatus="apiStatus"
      :recordsData="recordsData"
      :referenceData="referenceData"
      :linkColumnValue="null"
      :includeFilter="true"
      :includeRecordHeader="false"
      :filterData="filterData"
      :recordLink="recordLink"
      :showTable="showTable"
      @updatedFilterData="updateFilterData"
    >
      <template v-slot:callOutColumn>
        <div v-if="!isCRM" class="call-out call-to-action box">
          <div class="content-container">
            <figure>
              <img :src="referenceData.callOutIcon" alt="callout-icon" />
            </figure>
            <div id="calloutText" class="text-container" v-if="referenceData.callOutText && !checkSomeObjectPropertiesTrue(apiStatus.refData)" v-html="referenceData.callOutText" />
            <router-link :to="{ name: MY_COVER_MAKE_A_PAYMENT, query: { step: 1 } }" class="button crimson">Make a payment</router-link>
          </div>
        </div>
      </template>
      <template v-slot:paragraphData>
        <h2 v-if="!isCRM && showTable">Premium Activity</h2>
        <p v-if="!isCRM && showTable">Here's a record of premium activity on your membership.</p>
        <p v-if="pagination.totalNum > 0">
          Direct debit payments are deemed successful on the scheduled payment date. Dishonours/rejections will not be displayed for up to 2 business days after the direct debit
          attempt.
        </p>
        <div v-if="isCRM && !apiStatus.refData.loading && !apiStatus.refData.fail" class="call-out call-to-action">
          <h2>Policy premium contribution details</h2>
          <p v-if="contributionBreakdown.datePaidTo">
            This membership is covered until
            <strong>{{ contributionBreakdown.datePaidTo }}</strong>
          </p>
          <p v-else>Your first premium has not been paid</p>
          <p>
            The next payment is due
            <strong>{{ contributionBreakdown.nextContributionDate }}</strong>
          </p>
          <p class="no-margin-bottom">
            The premium contribution amount is
            <strong>{{ contributionBreakdown.premiumAmount }}</strong>
          </p>
          <ul>
            <li>
              Age based discount amount:
              <strong>{{ contributionBreakdown.abdAmount }}</strong>
            </li>
            <li>
              Rebate amount:
              <strong>{{ contributionBreakdown.rebateAmount }}</strong>
            </li>
            <li>
              Lifetime health cover amount:
              <strong>{{ contributionBreakdown.lhcAmount }}</strong>
            </li>
          </ul>
        </div>
      </template>
      <template v-slot:pagination>
        <Pagination isSPA @toPageNumber="paginationNavigation" :endPage="pagination.lastPageNum" :currentPageFromParent="pagination.pageNum" />
      </template>
    </RecordsView>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { GO_LIVE_DATE } from '@/constants';
import { getAccessLevel, getCoveredAs, getImageUrl, isDateFormat, formatAsCurrency, sortByObjectProperty, isBuildCRM } from '@/utils';
import { AccessLevel, FilterTypes } from '@/enums';
import { MY_COVER_MAKE_A_PAYMENT, CONTACT_US } from '@/router/routeNames';
import { setQueryString } from '@/utils/UrlUtils';
import { convertDateStringToText, reverseFormatFormDate, formatFormDate } from '@general/js/utils/dateTimeUtils';
import { checkSomeObjectPropertiesTrue } from '@general/js/utils/ObjectUtils';
import RecordsView from '@/components/RecordsView.vue';
import Pagination from '@general/js/components/Pagination.vue';
import PaymentsService from '@/services/MyCover/Payments';
import { useVuelidate } from '@vuelidate/core';

const isCRM = isBuildCRM();

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'Payments',

  components: {
    Pagination,
    RecordsView,
  },

  data() {
    return {
      MY_COVER_MAKE_A_PAYMENT,
      CONTACT_US,
      apiStatus: {
        refData: {
          fail: false,
          loading: false,
        },
        recordData: {
          fail: false,
          loading: false,
        },
        recordDocument: {
          fail: false,
          loading: false,
        },
      },
      recordsData: {
        headings: {
          referenceId: 'Reference',
          datePaid: 'Date',
          amountPaid: 'Amount',
          paymentDescription: 'Description',
        },
        data: [],
        noRecordsText: `Only payments received since ${reverseFormatFormDate(GO_LIVE_DATE)} are displayed. <br> No payments have been received for this membership.`,
      },
      paymentsHistoryData: [],
      paymentsFilteredData: [],
      referenceData: {
        informationText: undefined,
        callOutText: undefined,
        callOutIcon: undefined,
        paymentFlexCodeValues: [],
      },
      accessLevel: '',
      coveredAs: '',
      recordLink: {
        recordLinkFunction: this.fetchPaymentsDocument,
        recordLinkParams: 'Reference',
      },
      filterData: {
        filterName: 'premium activity',
        filterFunction: this.applyFilters,
        filterOptions: [],
        filterNoRecordsText: 'We cannot find any records that match your search. Please adjust your search criteria and try again.',
      },
      filterResult: {
        referenceNumber: '',
        startDate: '',
        endDate: '',
      },
      pagination: {
        pageNum: 1,
        pageSize: 10,
        totalNum: 0,
        lastPageNum: 1,
      },
      showTable: true,
      isCRM,
      contributionBreakdown: {
        datePaidTo: null,
        nextContributionDate: null,
        premiumAmount: null,
        abdAmount: null,
        rebateAmount: null,
        lhcAmount: null,
      },
    };
  },

  validations: {
    filterResult: {
      startDate: {
        isDateFormat(startDate) {
          if (startDate === '') {
            return true;
          }
          return isDateFormat(startDate);
        },
      },
      endDate: {
        isDateFormat(endDate) {
          if (endDate === '') {
            return true;
          }
          return isDateFormat(endDate);
        },
      },
    },
  },

  beforeMount() {
    if (!this.isCRM) {
      // always set pageNum url query param to 1 in MP
      setQueryString('pageNum', 1);
    }
  },

  mounted() {
    if (!isCRM) {
      this.getMemberDetails();
      this.getReferenceData();

      // Don't show premium activity (payment history) table if self-access member in MP
      if (this.accessLevel?.toLowerCase() === AccessLevel.SELF_ACCESS) {
        this.showTable = false;
      }
    } else {
      this.getCRMMemberNumber();
      this.fetchContributionBreakdown();
    }
    this.fetchPaymentsHistoryData();
    this.getFilteredOptions();
  },

  computed: {
    ...mapGetters({
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      memberships: VUEX_GENERAL.GETTERS.memberships,
      personCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
    }),
  },

  methods: {
    ...mapMutations({
      updateMemberNumber: VUEX_GENERAL.MUTATIONS.saveThisPolicyNumber,
    }),

    checkSomeObjectPropertiesTrue,

    getMemberDetails() {
      this.accessLevel = getAccessLevel(this.memberNumber, this.memberships);
      this.coveredAs = getCoveredAs(this.memberNumber, this.memberships);
    },

    async getReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        const refData = await PaymentsService.getReferenceData(this.memberNumber);
        if (refData.referenceData) {
          const ref = refData.referenceData;

          if (this.accessLevel?.toLowerCase() === AccessLevel.SELF_ACCESS) {
            if (ref.paymentsInformationSelfAccessText) {
              this.referenceData.informationText = ref.paymentsInformationSelfAccessText.informationText;
            }
          } else if (ref.paymentsInformationFullAccessText) {
            this.referenceData.informationText = ref.paymentsInformationFullAccessText.informationText;
          }

          if (ref.paymentsCallOutText && refData.datePaidTo) {
            this.referenceData.callOutText = ref.paymentsCallOutText.informationText;
            const datePaidToText = convertDateStringToText(refData.datePaidTo);
            this.referenceData.callOutText = this.referenceData.callOutText.replace(/#{paidToDate}/i, datePaidToText);
          } else if (ref.paymentsLaterCallOutText && refData.coverStartDate) {
            this.referenceData.callOutText = ref.paymentsLaterCallOutText.informationText;
            const coverStartDateText = convertDateStringToText(refData.coverStartDate);
            this.referenceData.callOutText = this.referenceData.callOutText.replace(/#{coverStartDate}/i, coverStartDateText);
          }
          if (ref.paymentActivityDescriptionOptions) {
            this.paymentFlexCodeValues = ref.paymentActivityDescriptionOptions;
          }
          if (ref.paymentsCallOutIcon) {
            this.referenceData.callOutIcon = getImageUrl(ref.paymentsCallOutIcon.informationText);
          }
        }
      } catch (error) {
        this.apiStatus.refData.fail = true;
        if (error.message) {
          logger.error(error.message);
        } else {
          logger.error(error);
        }
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async fetchPaymentsHistoryData() {
      this.apiStatus.recordData.loading = true;
      this.apiStatus.recordData.fail = false;
      if (this.showTable) {
        try {
          let data = await PaymentsService.getPayments(this.memberNumber);
          data = JSON.parse(JSON.stringify(data));
          if (data.error) {
            this.apiStatus.recordData.fail = true;
          } else if (data.payments) {
            this.paymentsHistoryData = data.payments.map((obj) => {
              return {
                referenceId: obj.referenceId,
                datePaid: reverseFormatFormDate(obj.datePaid),
                originalDate: obj.datePaid,
                amountPaid: formatAsCurrency(obj.amount),
                paymentDescription: obj.paymentDescription,
                paymentMethod: obj.paymentMethod,
                isBold: obj.paymentMethod ? obj.paymentMethod.includes('RFND') : false,
              };
            });

            this.paymentsHistoryData = sortByObjectProperty(this.paymentsHistoryData, 'originalDate', 'referenceId');
            this.paymentsFilteredData = this.paymentsHistoryData;
            this.pagination.totalNum = this.paymentsHistoryData.length;
            this.pagination.lastPageNum = Math.ceil(this.pagination.totalNum / this.pagination.pageSize);
            this.recordsData.data = this.paymentsHistoryData.slice(0, this.pagination.pageSize);
          }
        } catch (error) {
          this.apiStatus.recordData.fail = true;

          if (error.message) {
            logger.error(error.message);
          } else {
            logger.error(error);
          }
        } finally {
          this.apiStatus.recordData.loading = false;
        }
      } else {
        this.apiStatus.recordData.loading = false;
      }
    },

    async fetchContributionBreakdown() {
      this.apiStatus.refData.fail = false;
      this.apiStatus.refData.loading = true;

      try {
        const data = await PaymentsService.getContributionBreakdown(this.memberNumber);
        if (data.error) {
          this.apiStatus.refData.fail = true;
        } else if (data) {
          this.contributionBreakdown.datePaidTo = data.datePaidTo ? convertDateStringToText(data.datePaidTo) : null;
          this.contributionBreakdown.nextContributionDate = convertDateStringToText(data.nextContributionDate);
          this.contributionBreakdown.premiumAmount = formatAsCurrency(data.premiumAmount);
          this.contributionBreakdown.abdAmount = formatAsCurrency(data.abdAmount);
          this.contributionBreakdown.rebateAmount = formatAsCurrency(data.rebateAmount);
          this.contributionBreakdown.lhcAmount = formatAsCurrency(data.lhcAmount);
        }
      } catch (error) {
        this.apiStatus.refData.fail = true;
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    getFilteredOptions() {
      this.filterData.filterFunction = this.applyFilters;
      this.filterData.filterOptions = [
        {
          filterHeading: 'Reference',
          filterType: FilterTypes.TEXT_INPUT,
          filterPlaceholder: 'e.g. 12456',
          filterResult: this.filterResult.referenceNumber,
        },
        {
          filterHeading: 'Date from',
          filterType: FilterTypes.DATE_INPUT,
          filterPlaceholder: 'DD / MM / YYYY',
          filterResult: this.filterResult.startDate,
          filterResultInvalid: this.v$.filterResult.startDate.$invalid,
          filterInvalidMessage: 'Please enter a valid date',
        },
        {
          filterHeading: 'Date to',
          filterType: FilterTypes.DATE_INPUT,
          filterPlaceholder: 'DD / MM / YYYY',
          filterResult: this.filterResult.endDate,
          filterResultInvalid: this.v$.filterResult.endDate.$invalid,
          filterInvalidMessage: `Please enter a valid date`,
        },
      ];
    },

    updateFilterData(filterData) {
      this.filterData = filterData;

      this.filterResult.referenceNumber = filterData.filterOptions[0].filterResult;
      this.filterResult.startDate = filterData.filterOptions[1].filterResult;
      filterData.filterOptions[1].filterResultInvalid = this.v$.filterResult.startDate.$invalid;
      this.filterResult.endDate = filterData.filterOptions[2].filterResult;
      filterData.filterOptions[2].filterResultInvalid = this.v$.filterResult.endDate.$invalid;
    },

    applyFilters() {
      // add query string
      this.pagination.pageNum = 1;

      this.paymentsFilteredData = this.paymentsHistoryData.filter((payment) => {
        if (this.filterResult.referenceNumber.startsWith('All ') || this.filterResult.referenceNumber === '') {
          return true;
        }
        return String(payment.referenceId).includes(this.filterResult.referenceNumber);
      });

      this.paymentsFilteredData = this.paymentsFilteredData.filter((payment) => {
        const date = formatFormDate(payment.datePaid);
        const startDate = formatFormDate(this.filterResult.startDate);
        const endDate = formatFormDate(this.filterResult.endDate);

        // check if the date input is empty
        if (this.filterResult.startDate === '' && this.filterResult.endDate === '') {
          return true;
        }
        if (this.filterResult.endDate === '' && this.filterResult.startDate !== '') {
          return date >= startDate;
        }
        if (this.filterResult.startDate === '' && this.filterResult.endDate !== '') {
          return date <= endDate;
        }

        return date >= startDate && date <= endDate;
      });

      this.pagination.totalNum = this.paymentsFilteredData.length;
      this.pagination.lastPageNum = Math.ceil(this.pagination.totalNum / this.pagination.pageSize);
      this.recordsData.data = this.paymentsFilteredData.slice(0, this.pagination.pageSize);
    },

    paginationNavigation(pageNum) {
      this.pagination.pageNum = Number(pageNum);

      if (this.paymentsFilteredData.length > 0) {
        this.recordsData.data = this.paymentsFilteredData.slice((this.pagination.pageNum - 1) * this.pagination.pageSize, this.pagination.pageNum * this.pagination.pageSize);
      } else {
        this.recordsData.data = this.paymentsHistoryData.slice((this.pagination.pageNum - 1) * this.pagination.pageSize, this.pagination.pageNum * this.pagination.pageSize);
      }
    },

    getCRMMemberNumber() {
      if (window.parent.getContext) {
        if (window.parent.getContext.getAttribute('dhl_membershipid')) {
          this.updateMemberNumber(window.parent.getContext.getAttribute('dhl_membershipid').getValue());
        }
      }
    },

    getImageUrl,
  },
};
</script>
<style lang="scss" scoped>
:deep(#side-content-paragraph) {
  max-width: 680px;
}

:deep(.call-out) {
  h2 {
    text-align: left;
  }
}
</style>
